import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead, CourseSchema, FAQSchema } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Pencil, Users, GraduationCap, MapPin, CheckCircle, ArrowRight, Eye } from 'lucide-react';

const DrawingClassesSanAntonio = () => {
  const faqs = [
    {
      question: "What drawing classes are available in San Antonio?",
      answer: "The Coppini Academy offers figure drawing, portrait drawing, cast drawing, and foundational sketching classes in San Antonio. We focus on classical techniques using graphite, charcoal, and conte."
    },
    {
      question: "Is drawing important before learning to paint?",
      answer: "Yes, drawing is the foundation of all visual arts. Our curriculum emphasizes strong drawing skills as the basis for painting, helping students develop accurate observation and mark-making abilities."
    },
    {
      question: "Do you offer life drawing sessions in San Antonio?",
      answer: "Yes, we offer regular open studio sessions featuring live models for figure drawing practice. These sessions are available to members and non-members alike."
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      <SEOHead
        title="Drawing Classes in San Antonio, TX"
        description="Master classical drawing techniques at The Coppini Academy of Fine Art in San Antonio. Figure drawing, portrait sketching, and foundational art classes. Enroll now!"
        keywords="drawing classes San Antonio, figure drawing San Antonio TX, learn to draw San Antonio, art classes San Antonio, sketching lessons Texas, life drawing San Antonio"
        canonicalPath="/drawing-classes-san-antonio"
      />
      <CourseSchema
        name="Classical Drawing Classes in San Antonio"
        description="Learn figure drawing, portrait sketching, and classical drawing techniques at San Antonio's premier fine art academy."
      />
      <FAQSchema faqs={faqs} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1 bg-cafa-red/20 text-cafa-gold text-sm font-medium rounded-full mb-6">
              Classical Art Training Since 1945
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight">
              Drawing Classes in San Antonio, TX
            </h1>
            <p className="text-xl text-stone-300 mb-8 leading-relaxed">
              Develop the foundational skill every great artist needs. Our classical drawing 
              program teaches you to see accurately, understand form, and create compelling 
              work from observation and imagination.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/classes/adult">View Drawing Classes <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/classes/open-sessions">Open Studio Sessions</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The Importance of Drawing */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-6">
                Drawing: The Foundation of All Visual Art
              </h2>
              <p className="text-lg text-stone-600 mb-4 leading-relaxed">
                At The Coppini Academy of Fine Art, we believe that strong drawing skills 
                are essential for every artist. Drawing trains your eye to observe accurately, 
                your hand to respond precisely, and your mind to understand the three-dimensional 
                world on a two-dimensional surface.
              </p>
              <p className="text-lg text-stone-600 mb-6 leading-relaxed">
                Our San Antonio drawing classes follow the classical atelier tradition, 
                emphasizing careful observation, accurate proportion, and sensitive rendering 
                of light and shadow. Whether you aspire to paint, sculpt, or simply sketch 
                for pleasure, our drawing curriculum will transform how you see the world.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Eye, text: "Train Your Eye" },
                  { icon: Pencil, text: "Master Technique" },
                  { icon: GraduationCap, text: "Classical Methods" },
                  { icon: Users, text: "Small Class Sizes" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center text-stone-700">
                    <item.icon className="text-cafa-red mr-2" size={20} />
                    <span className="font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-stone-200 rounded-lg aspect-[4/3] flex items-center justify-center">
              <Pencil size={80} className="text-stone-400" />
            </div>
          </div>
        </div>
      </section>

      {/* Drawing Programs */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4 text-center">
            Drawing Classes in San Antonio
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto text-center mb-12">
            From beginner fundamentals to advanced figure studies, our drawing curriculum 
            provides a complete classical education.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Beginning Drawing",
                level: "Beginner",
                desc: "Start your artistic journey with fundamental drawing skills. Learn to see like an artist and translate observation to paper.",
                topics: ["Line quality", "Proportion & measurement", "Basic shading", "Simple compositions"]
              },
              {
                title: "Figure Drawing",
                level: "All Levels",
                desc: "Study the human figure from life models. Develop gesture, proportion, and anatomical understanding.",
                topics: ["Gesture drawing", "Figure proportion", "Anatomical landmarks", "Value rendering"]
              },
              {
                title: "Portrait Drawing",
                level: "Intermediate",
                desc: "Learn to capture likeness and character in portrait drawings. Study facial structure and expression.",
                topics: ["Facial proportions", "Planes of the head", "Feature studies", "Capturing likeness"]
              },
              {
                title: "Cast Drawing",
                level: "All Levels",
                desc: "Study classical sculpture casts to develop precise observation and refined rendering techniques.",
                topics: ["Sight-size method", "Value control", "Edge handling", "Form modeling"]
              },
              {
                title: "Perspective Drawing",
                level: "Beginner to Intermediate",
                desc: "Master linear perspective for architectural subjects, interiors, and complex spatial compositions.",
                topics: ["One-point perspective", "Two-point perspective", "Atmospheric perspective", "Urban sketching"]
              },
              {
                title: "Open Studio Sessions",
                level: "All Levels",
                desc: "Practice figure drawing from live models in our supervised open studio sessions.",
                topics: ["Long poses", "Quick gestures", "Various lighting", "Peer feedback"]
              }
            ].map((course, idx) => (
              <Card key={idx} className="border-stone-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-stone-900">{course.title}</h3>
                    <span className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded">{course.level}</span>
                  </div>
                  <p className="text-stone-600 text-sm mb-4">{course.desc}</p>
                  <ul className="space-y-1">
                    {course.topics.map((topic, i) => (
                      <li key={i} className="text-sm text-stone-600 flex items-center">
                        <CheckCircle size={12} className="text-cafa-red mr-2 flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Materials & Approach */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              Classical Drawing Materials & Methods
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We teach drawing using time-tested materials and techniques that have 
              produced master draftsmen for centuries.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Graphite Pencil",
                desc: "The most versatile drawing tool. Learn to control value, line weight, and texture with graphite pencils from 6H to 8B."
              },
              {
                title: "Charcoal",
                desc: "Create bold, expressive drawings with vine and compressed charcoal. Ideal for figure drawing and tonal studies."
              },
              {
                title: "Conté Crayon",
                desc: "Traditional European drawing material perfect for figure studies. Available in sanguine, sepia, and black."
              }
            ].map((material, idx) => (
              <Card key={idx} className="border-stone-200">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-stone-900 mb-3">{material.title}</h3>
                  <p className="text-stone-600">{material.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location CTA */}
      <section className="py-16 lg:py-20 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">
                Learn Drawing in San Antonio
              </h2>
              <p className="text-stone-300 mb-6 leading-relaxed">
                Our Monte Vista studio provides the perfect environment for focused drawing practice. 
                Large north-facing windows provide consistent natural light, while our collection 
                of classical casts offers excellent subjects for study.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <MapPin className="text-cafa-red mr-3 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-medium">The Coppini Academy of Fine Art</p>
                    <p className="text-stone-400">115 Melrose Place, San Antonio, TX 78212</p>
                  </div>
                </div>
              </div>
              <Button asChild className="bg-cafa-red hover:bg-cafa-red-900">
                <Link to="/contact">Visit Our Studio</Link>
              </Button>
            </div>
            <div className="space-y-6">
              <div className="rounded-lg overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=115+Melrose+Place,+San+Antonio,+TX+78212&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Coppini Academy Location - Drawing Classes San Antonio"
                  className="grayscale"
                ></iframe>
              </div>
              <div className="bg-stone-800 rounded-lg p-8">
                <h3 className="text-xl font-semibold mb-4">Start Drawing Today</h3>
              <p className="text-stone-400 mb-6">
                Whether you&apos;re picking up a pencil for the first time or looking to 
                refine your skills, our San Antonio drawing classes will help you achieve your goals.
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full bg-white text-stone-900 hover:bg-stone-100">
                  <Link to="/classes/adult">View Class Schedule</Link>
                </Button>
                <Button asChild variant="outline" className="w-full border-white text-white hover:bg-white/10">
                  <Link to="/classes/open-sessions">Join Open Studio</Link>
                </Button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-8 text-center">
            Drawing Class FAQs
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-stone-200 pb-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{faq.question}</h3>
                <p className="text-stone-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-cafa-red text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif mb-4">
            Develop Your Drawing Skills
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join San Antonio&apos;s most respected drawing program and build the foundation for your artistic journey.
          </p>
          <Button asChild size="lg" className="bg-white text-cafa-red hover:bg-stone-100">
            <Link to="/classes/adult">Enroll in Drawing Classes</Link>
          </Button>
        </div>
      </section>
    </main>
  );
};

export default DrawingClassesSanAntonio;
